import { Settings } from 'lucide-react';
import { memo, useEffect, useRef, useState } from 'react';
import { t } from '../../../i18n';
import { store } from '../../../state/store';

const DRIVING_SPEECH_RATES = [0.85, 1, 1.15, 1.3, 1.5];

interface DrivingModeSettingsProps {
  audioOutputSupported: boolean;
  audioOutputOptions: { deviceId: string; label: string }[];
  speechRate: number;
  audioOutputDeviceId?: string;
  drivingCueSoundsEnabled: boolean;
}

function formatSpeechRate(rate: number): string {
  const fixed = rate.toFixed(2);
  return fixed.endsWith('00')
    ? `${Math.round(rate)}x`
    : fixed.endsWith('0')
      ? `${rate.toFixed(1)}x`
      : `${fixed}x`;
}

export const DrivingModeSettings = memo(function DrivingModeSettings({
  audioOutputSupported,
  audioOutputOptions,
  speechRate,
  audioOutputDeviceId,
  drivingCueSoundsEnabled,
}: DrivingModeSettingsProps) {
  const [showSettings, setShowSettings] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showSettings) return;

    const root = settingsRef.current?.getRootNode() as Document | ShadowRoot | null;

    const handlePointerDown = (event: Event) => {
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      if (settingsRef.current && !path.includes(settingsRef.current)) {
        setShowSettings(false);
      }
    };

    const handleKeyDown = (event: Event) => {
      if ((event as KeyboardEvent).key === 'Escape') {
        setShowSettings(false);
      }
    };

    root?.addEventListener('pointerdown', handlePointerDown);
    root?.addEventListener('keydown', handleKeyDown);

    return () => {
      root?.removeEventListener('pointerdown', handlePointerDown);
      root?.removeEventListener('keydown', handleKeyDown);
    };
  }, [showSettings]);

  function handleSpeechRateToggle(): void {
    const currentRate = store.getState().drivingMode.speechRate || 1;
    const currentIndex = DRIVING_SPEECH_RATES.findIndex(
      (rate) => Math.abs(rate - currentRate) < 0.001,
    );
    const nextRate = DRIVING_SPEECH_RATES[
      (currentIndex + 1 + DRIVING_SPEECH_RATES.length) % DRIVING_SPEECH_RATES.length
    ];
    store.setDrivingMode({ speechRate: nextRate });
  }

  return (
    <div className="driving-settings-anchor" ref={settingsRef}>
      <button
        type="button"
        className="driving-settings-trigger"
        aria-label={t('drivingMode')}
        aria-expanded={showSettings}
        aria-haspopup="dialog"
        onClick={() => setShowSettings(!showSettings)}
      >
        <Settings className="driving-settings-icon" />
      </button>
      {showSettings && (
        <div className="driving-collapse-actions" role="dialog" aria-label={t('drivingMode')}>
          <button
            className="driving-speed-btn"
            onClick={handleSpeechRateToggle}
            title={`${t('drivingModeSpeed')}: ${formatSpeechRate(speechRate)}`}
          >
            <span className="driving-speed-btn-label">{t('drivingModeSpeed')}</span>
            <span className="driving-speed-btn-value">{formatSpeechRate(speechRate)}</span>
          </button>
          {audioOutputSupported && (
            <label className="driving-audio-output-control">
              <span className="driving-audio-output-label">{t('drivingModeAudioOutput')}</span>
              <select
                className="driving-audio-output-select"
                value={audioOutputDeviceId || ''}
                onChange={(e) => {
                  const nextDeviceId = e.target.value || undefined;
                  store.setDrivingMode({ audioOutputDeviceId: nextDeviceId });
                }}
              >
                <option value="">{t('drivingModeAudioOutputDefault')}</option>
                {audioOutputOptions.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button
            className={`driving-cue-toggle-btn ${drivingCueSoundsEnabled ? '' : 'driving-cue-toggle-btn-off'}`}
            onClick={() => {
              store.setDrivingMode({ drivingCueSoundsEnabled: !drivingCueSoundsEnabled });
            }}
            title={t('drivingModeCueSounds')}
          >
            {drivingCueSoundsEnabled
              ? t('drivingModeCueSoundsOn')
              : t('drivingModeCueSoundsOff')}
          </button>
        </div>
      )}
    </div>
  );
});
